import { useEffect, useState } from "react";
import JobsService from "../../services/job.service";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { classNames } from "../../utils/classNamesUtils";
import { formatToShortCst } from "../../utils/formatToShortCst";
import { useSocket } from "../common/socket-context/SocketContext";
import Table, { Column } from "../common/table";
import StaffService from "../../services/staff.service";

const statusStyles = {
  COMPLETED: "bg-green-100 text-green-800",
  PENDING: "bg-yellow-100 text-yellow-800",
  PENDING_MANAGER_APPROVAL: "bg-yellow-100 text-yellow-800",
  PENDING_OPERATOR_APPROVAL: "bg-yellow-100 text-yellow-800",
  ASSIGNED: "bg-blue-100 text-blue-800",
  UNASSIGNED: "bg-gray-100 text-red-800",
};

const statusEmoji = {
  COMPLETED: '✅',
  PENDING: '⏳',
  PENDING_MANAGER_APPROVAL: '⏳',
  PENDING_OPERATOR_APPROVAL: '⏳',
  ASSIGNED: '🔵',
  UNASSIGNED: '🔴',
};

export default function JobTable() {
  const [rows, setRows] = useState<any[]>([]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const socket = useSocket();

  useEffect(() => {
      if (!socket) return;

      socket.on(`job.status.updated`, fetchData);

      return () => {
          socket.off('job.status.updated');
      };
  }, [socket]);

  const columns: Column<any>[] = [
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('jobs.jobType')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => (
        <td key={colI} className="px-6 py-2 text-left text-xs text-gray-500">
          {row.jobType}
        </td>
      ),
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('jobs.status')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => {
        const isMobile = window.innerWidth <= 768;

        return (
          <td key={colI} className="whitespace-nowrap px-6 py-2 text-left text-xs text-gray-500 md:block">
            {isMobile ? (
              <span className="ml-2 text-xs text-gray-500">
                {/* @ts-ignore */}
                {statusEmoji[row.status]}
              </span>
            ) : <span
              className={classNames(
                // @ts-ignore
                statusStyles[row.status],
                "inline-flex items-center px-2.5 py-0.5 rounded-full text-left text-xs font-medium capitalize"
              )}
            >
              {row.status}
            </span>}
          </td>
        )
      },
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('jobs.assignee')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => (
        <td key={colI} className="px-6 py-2 text-left text-xs text-gray-500">
          {typeof row.assignee === 'string' ? row.assignee : `${row.assignee?.firstName || ''} ${row.assignee?.lastName || ''}`.trim() || 'N/A'}
        </td>
      ),
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('jobs.createdAt')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => (
        <td key={colI} className="px-6 py-2 text-left text-xs text-gray-500">
          {formatToShortCst(row.createdAt)}
        </td>
      ),
    },
  ];

  const handleRowClick = (jobId: string) => {
    navigate(`/jobs/${jobId}`);
  };

  const fetchData = async () => {
    const response = await JobsService.searchJobs();
    const data = await response.json();
    // For each job, if assignee is string, fetch the staff details
    const jobsWithAssignees = await Promise.all(data.map(async (job: any) => {
      if (typeof job.assignee === 'string' && job.assignee) {
        try {
          const staffResponse = await StaffService.getStaffById(job.assignee);
          const staff = await staffResponse.json();
          return { ...job, assignee: staff };
        } catch (error) {
          console.error('Error fetching assignee:', error);
          return job;
        }
      }
      return job;
    }));
    setRows(jobsWithAssignees);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Table
      columns={columns}
      rows={rows}
      title={t('jobs.title')}
      currentPage={1}
      pageSize={1}
      totalRows={1}
      onRowClick={(row) => handleRowClick(row._id)}
    /> 
  )
}